import { useState } from 'react';
import type { TextAlign, VerticalAlign } from '../../doc/schema';
import { ColorPicker } from '../../ui/ColorPicker';
import {
  IconAlignCenter,
  IconAlignLeft,
  IconAlignRight,
  IconBold,
  IconItalic,
  IconValignBottom,
  IconValignMiddle,
  IconValignTop,
} from '../../ui/icons';
import { GOOGLE_FONTS } from './fonts';
import type { TextStyle } from './textStyle';

const CUSTOM = '__custom__';

/** Font family dropdown: default UI font, the curated Google Fonts, or any family typed by name. */
export function FontSelect({ value, onChange }: { value: string | null; onChange: (font: string | null) => void }) {
  const known = value === null || (GOOGLE_FONTS as readonly string[]).includes(value);
  return (
    <select
      className="font-select"
      value={value ?? ''}
      onChange={(e) => {
        const v = e.target.value;
        if (v === CUSTOM) {
          const name = prompt('Google Fonts family name (as on fonts.google.com)', value ?? '');
          if (name && name.trim()) onChange(name.trim());
          return;
        }
        onChange(v === '' ? null : v);
      }}
      title="Font"
    >
      <option value="">Default (mono)</option>
      {!known && value && <option value={value}>{value}</option>}
      {GOOGLE_FONTS.map((f) => (
        <option key={f} value={f}>
          {f}
        </option>
      ))}
      <option value={CUSTOM}>Other Google font…</option>
    </select>
  );
}

const SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 64, 80, 96, 128];

/** Inspector controls for block-level text style. `inlineHint` notes that rich text has per-selection formatting too. */
export function TextStyleFields({
  value,
  onChange,
  showBoldItalic = true,
  defaultColorLabel = 'theme',
}: {
  value: TextStyle;
  onChange: (patch: Partial<TextStyle>) => void;
  showBoldItalic?: boolean;
  defaultColorLabel?: string;
}) {
  const [sizeText, setSizeText] = useState<string | null>(null);
  const commitSize = (raw: string) => {
    setSizeText(null);
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 4 && n <= 400 && n !== value.fontSize) onChange({ fontSize: n });
  };
  return (
    <>
      <div className="insp-label">Text</div>
      <FontSelect value={value.font} onChange={(font) => onChange({ font })} />
      <div className="text-row">
        <input
          className="size-input"
          type="number"
          list="vds-font-sizes"
          min={4}
          max={400}
          value={sizeText ?? value.fontSize}
          onChange={(e) => setSizeText(e.target.value)}
          onBlur={(e) => commitSize(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            e.stopPropagation();
          }}
          title="Font size"
        />
        <datalist id="vds-font-sizes">
          {SIZES.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        {showBoldItalic && (
          <>
            <button type="button" className={`btn btn-icon${value.bold ? ' is-active' : ''}`} title="Bold" onClick={() => onChange({ bold: !value.bold })}>
              <IconBold />
            </button>
            <button type="button" className={`btn btn-icon${value.italic ? ' is-active' : ''}`} title="Italic" onClick={() => onChange({ italic: !value.italic })}>
              <IconItalic />
            </button>
          </>
        )}
      </div>
      {/* alignment on its own row: with size, bold and italic it no longer fits the panel width */}
      <div className="text-row">
        {(
          [
            ['left', IconAlignLeft],
            ['center', IconAlignCenter],
            ['right', IconAlignRight],
          ] as [TextAlign, () => React.JSX.Element][]
        ).map(([a, Icon]) => (
          <button key={a} type="button" className={`btn btn-icon${value.align === a ? ' is-active' : ''}`} title={`Align ${a}`} onClick={() => onChange({ align: a })}>
            <Icon />
          </button>
        ))}
        {value.valign !== undefined && (
          <>
            <span className="text-row-sep" aria-hidden />
            {(
              [
                ['top', IconValignTop],
                ['middle', IconValignMiddle],
                ['bottom', IconValignBottom],
              ] as [VerticalAlign, () => React.JSX.Element][]
            ).map(([v, Icon]) => (
              <button key={v} type="button" className={`btn btn-icon${value.valign === v ? ' is-active' : ''}`} title={`Text at the ${v}`} onClick={() => onChange({ valign: v })}>
                <Icon />
              </button>
            ))}
          </>
        )}
      </div>
      <div className="insp-label">Text colour ({value.color ? value.color : defaultColorLabel})</div>
      <ColorPicker value={value.color ?? 'none'} onChange={(c) => onChange({ color: c === 'none' ? null : c })} allowNone />
    </>
  );
}
